import { useEffect, useState } from 'react';

import { AppIcon, EducationalInvestment, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.survey.output.blueprintSection.educationalInvestments.detail;

type EducationalInvestmentsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  target: EducationalInvestment[];
  others: EducationalInvestment[];
  /** Auto-expand only this investment when the modal opens. */
  initialExpandedId?: number | null;
}>;

export function EducationalInvestmentsModal({
  open,
  onClose,
  target,
  others,
  initialExpandedId,
}: EducationalInvestmentsModalProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(
    () => new Set(initialExpandedId != null ? [initialExpandedId] : []),
  );

  useEffect(() => {
    if (open) {
      setExpandedIds(new Set(initialExpandedId != null ? [initialExpandedId] : []));
    }
  }, [open, initialExpandedId]);

  const handleToggle = (id: number) => {
    setExpandedIds((prev) => {
      const current = new Set(prev);

      if (current.has(id)) current.delete(id);
      else current.add(id);

      return current;
    });
  };

  const targetIds = new Set(target.map((t) => t.id));

  const allInvestments = [...target, ...others];

  return (
    <InfoModal
      open={open}
      onClose={onClose}
      width="sm"
      icon="AnnotationInfo"
      hideCircleImage
      headerContent={<Typography variant="display-xs-semibold">{STRINGS.heading}</Typography>}
      bodyContent={
        <Box sx={listSx}>
          {allInvestments.map((inv) => {
            const expanded = expandedIds.has(inv.id);

            return (
              <Box
                key={inv.id}
                id={`education-investments-modal-row-${inv.id}`}
                sx={rowSx(expanded)}
                onClick={() => handleToggle(inv.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleToggle(inv.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
              >
                <Box sx={rowHeaderSx}>
                  <AppIcon
                    iconName={targetIds.has(inv.id) ? 'Check' : 'XClose'}
                    diameter="20px"
                    color={targetIds.has(inv.id) ? 'Brand/600' : 'Colors/Text/text-quaternary'}
                  />
                  <Typography variant="text-md-medium">{inv.name}</Typography>
                </Box>
                <Box sx={descriptionContainerSx(expanded)}>
                  <Box sx={expandedContentSx}>
                    <Typography variant="text-sm-regular" sx={descriptionTextSx}>
                      {inv.description}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      }
    />
  );
}

// --- Styles ---

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const rowSx =
  (isExpanded: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-md'],
    padding: `${backrs.spacing['spacing-lg']} ${backrs.spacing['spacing-xl']}`,
    borderRadius: backrs.radius['radius-lg'],
    border: `1px solid ${backrs.colors[isExpanded ? 'Brand/500' : 'Colors/Border/border-secondary']}`,
    cursor: 'pointer',
  });

const rowHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const descriptionContainerSx =
  (isExpanded: boolean): SxProps<Theme> =>
  () => ({
    display: 'grid',
    gridTemplateRows: isExpanded ? '1fr' : '0fr',
    opacity: isExpanded ? 1 : 0,
    transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out',
    '& > *': {
      overflow: 'hidden',
    },
  });

const expandedContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
});

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});
